// ── WHAT KIND OF GAP IS THIS? ───────────────────────────────────────────────
//
// #4779 amendment 6, finding 3.
//
// THE DEFECT THIS EXISTS TO PREVENT. Amendment 1 routed every
// `factPacket.gapIds` entry onto the evidence-gap surface, where each one
// acquires an "Approved source readiness / Additional approved source material
// is needed" card. That is right for a declared absence of approved source
// material and wrong for everything else. The finance value-gap path emits
// `composite_total_unproven` on EVERY successful packet — the producer's own
// hold on summing lever contributions into one composite total, a separation
// question about arithmetic rather than a statement about the tenant's data —
// and `hasMissingProof` correctly stays false for it. The reader still got a
// card telling them to go connect a source that was already connected.
//
// THE RULE, mirroring the typed-cause scoping amendment 5 applied to withheld
// figures: the PACKET'S TYPED CLASSIFICATION decides, never the id string. No
// pattern, no substring, no registry of known ids — the producer that raised
// the gap declares what kind it is, or the default stands.
//
// THE DEFAULT IS `evidence`, deliberately. A producer that has not been
// migrated declares nothing, and its gaps keep landing exactly where they land
// today. Opting a gap OFF the evidence surface takes an explicit, auditable
// declaration; nothing is ever quietly removed from a reader's view because a
// classification was missing.
//
// This file carries NO company data and NO arithmetic. It is a vocabulary.

/**
 * The kind of hold a producer's fact-packet gap represents.
 *
 * `evidence`           — approved source material is absent or not connected
 *                        for this figure. Operator-actionable: connect the
 *                        source. Routes to the evidence-gap surface.
 * `separation_receipt` — the producer can compute the parts but will not
 *                        publish a combined total until an approved
 *                        deconfliction / separation receipt proves the parts do
 *                        not double-count. Nothing about the tenant's evidence
 *                        is missing; telling them to connect a source would be
 *                        a fabricated cause. Renders as its own named card.
 */
export const PRODUCER_GAP_CLASSES = ['evidence', 'separation_receipt'] as const;

export type ProducerGapClass = (typeof PRODUCER_GAP_CLASSES)[number];

/** The class a producer's gap takes when it declares none. */
export const DEFAULT_PRODUCER_GAP_CLASS: ProducerGapClass = 'evidence';

/**
 * The declared class for one gap id, or the default.
 *
 * A SHAPE read of a declaration, never an inference from the id. Anything that
 * is not a member of the closed vocabulary — a typo, a number, a stale label —
 * falls back to the default rather than inventing a third kind, so a malformed
 * declaration can only ever leave a gap where it already was.
 */
export function producerGapClass(value: unknown): ProducerGapClass {
  return (PRODUCER_GAP_CLASSES as readonly string[]).includes(
    typeof value === 'string' ? value.trim() : '',
  )
    ? ((value as string).trim() as ProducerGapClass)
    : DEFAULT_PRODUCER_GAP_CLASS;
}

/** The reader-facing card title for a non-evidence gap kind. */
export const PRODUCER_GAP_CLASS_CARD_TITLE: Readonly<
  Record<Exclude<ProducerGapClass, 'evidence'>, string>
> = Object.freeze({
  separation_receipt: 'Composite total pending a separation receipt',
});
